<script lang="ts">
/**
 * «Тёплое» окно: пока пользователь скользит по соседним элементам (иконки рейла,
 * кнопки тулбара), следующий tooltip открывается сразу, без задержки. Общее для всех
 * экземпляров, поэтому живёт на уровне модуля.
 */
const WARMUP_MS = 500;
let lastHiddenAt = 0;

/**
 * Модальность ввода: tooltip по фокусу показываем только с клавиатуры. Клик мышью
 * тоже даёт фокус, но после клика tooltip должен погаснуть, а не вспыхнуть заново.
 */
let isKeyboardModality = true;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'keydown',
    () => {
      isKeyboardModality = true;
    },
    true,
  );
  document.addEventListener(
    'pointerdown',
    () => {
      isKeyboardModality = false;
    },
    true,
  );
}
</script>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, useId, useSlots, watch } from 'vue';

import { DOCUMENT_FOCUSABLE_SELECTOR } from '@/shared/lib';

import type { TooltipProps } from './types';

import { useTooltip, useTooltipInteractions } from './composables/use-tooltip';

const props = withDefaults(defineProps<TooltipProps>(), {
  placement: 'auto',
  disabled: false,
  delay: 400,
});

const slots = useSlots();
const tooltipId = `tooltip-${useId()}`;
const triggerRef = ref<HTMLElement>();
const tooltipRef = ref<HTMLElement>();
const showTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
const hasFocusableDescendant = ref(false);
const hasContent = computed(() => Boolean(props.content) || Boolean(slots.content));
const triggerTabindex = computed(() =>
  hasContent.value && !props.disabled && !hasFocusableDescendant.value ? 0 : undefined,
);

const { isVisible, position, show, hide } = useTooltip(toRef(props, 'placement'));

watch(isVisible, (visible) => {
  if (!visible) {
    lastHiddenAt = Date.now();
  }
});
const { isTouchOpen, shouldRemainVisible, setPointerInside, setFocusInside, toggleTouch, dismiss, escape } =
  useTooltipInteractions();

const clearShowTimeout = (): void => {
  if (showTimeoutId.value === null) {
    return;
  }

  clearTimeout(showTimeoutId.value);
  showTimeoutId.value = null;
};

const canShow = (): boolean => !props.disabled && Boolean(triggerRef.value) && hasContent.value;

// Погасить сразу: отменить отложенный показ, сбросить touch/фокус-состояние, скрыть.
const dismissAndHide = (): void => {
  clearShowTimeout();
  dismiss();
  hide();
};

const showImmediately = async (): Promise<void> => {
  clearShowTimeout();

  if (!canShow()) {
    return;
  }

  isVisible.value = true;
  await nextTick();

  if (tooltipRef.value && triggerRef.value) {
    show(triggerRef.value, tooltipRef.value);
  }
};

const handleMouseEnter = (): void => {
  setPointerInside(true);

  if (!canShow() || isTouchOpen.value) {
    return;
  }

  clearShowTimeout();
  showTimeoutId.value = setTimeout(
    () => {
      showTimeoutId.value = null;
      void showImmediately();
    },
    Date.now() - lastHiddenAt < WARMUP_MS ? 0 : props.delay,
  );
};

const handleMouseLeave = (): void => {
  setPointerInside(false);
  clearShowTimeout();

  if (!shouldRemainVisible.value) {
    hide();
  }
};

const handleFocusIn = (): void => {
  if (!isKeyboardModality) {
    return;
  }

  setFocusInside(true);
  void showImmediately();
};

// Клик по триггеру гасит tooltip: подсказка сделала своё дело. Touch не трогаем —
// там tooltip открывается тапом (см. handleTouchEnd).
const handlePointerDown = (event: PointerEvent): void => {
  if (event.pointerType === 'touch') {
    return;
  }

  dismissAndHide();
};

const handleFocusOut = (event: FocusEvent): void => {
  const nextTarget = event.relatedTarget;

  if (nextTarget instanceof Node && triggerRef.value?.contains(nextTarget)) {
    return;
  }

  setFocusInside(false);

  if (!shouldRemainVisible.value) {
    hide();
  }
};

const handleTouchEnd = (): void => {
  if (!canShow()) {
    return;
  }

  if (toggleTouch()) {
    void showImmediately();

    return;
  }

  hide();
};

const handleDocumentPointerDown = (event: PointerEvent): void => {
  const target = event.target;

  if (!isTouchOpen.value || (target instanceof Node && triggerRef.value?.contains(target))) {
    return;
  }

  dismissAndHide();
};

const handleDocumentKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'Escape' || !isVisible.value) {
    return;
  }

  if (!escape()) {
    return;
  }

  clearShowTimeout();
  hide();
};

const updateFocusableDescendant = (): void => {
  hasFocusableDescendant.value = Boolean(triggerRef.value?.querySelector(DOCUMENT_FOCUSABLE_SELECTOR));
};

watch([() => props.disabled, hasContent], ([disabled, contentAvailable]) => {
  if (!disabled && contentAvailable) {
    return;
  }

  dismissAndHide();
});

useEventListener(document, 'pointerdown', handleDocumentPointerDown);
useEventListener(document, 'keydown', handleDocumentKeydown);

onMounted(() => {
  updateFocusableDescendant();
});

onBeforeUnmount(() => {
  clearShowTimeout();
});
</script>

<template>
  <div
    ref="triggerRef"
    class="tooltip-trigger inline-block"
    role="group"
    :tabindex="triggerTabindex"
    :aria-describedby="isVisible ? tooltipId : undefined"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @pointerdown="handlePointerDown"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @touchend.passive="handleTouchEnd"
  >
    <slot />
  </div>

  <Teleport to="body">
    <div
      v-if="isVisible && hasContent"
      :id="tooltipId"
      ref="tooltipRef"
      role="tooltip"
      data-modal-ignore
      class="tooltip-container"
      :class="`tooltip-${position.placement}`"
      :style="{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }"
    >
      <div class="tooltip-content">
        <slot name="content">
          {{ content }}
        </slot>
      </div>
      <div class="tooltip-arrow"></div>
    </div>
  </Teleport>
</template>

<style scoped>
.tooltip-container {
  position: absolute;
  z-index: 9999;
  pointer-events: none;
}

.tooltip-content {
  position: relative;
  z-index: 1;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: var(--tooltip-bg);
  color: var(--tooltip-text-color);
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  max-width: 250px;
  word-wrap: break-word;
}

/* Стрелка — квадрат 8×8, повёрнутый на 45°; центр лежит на кромке контейнера,
   наружу выступает половина (4px). Скругление только у выступающего угла. */
.tooltip-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: var(--tooltip-bg);
  transform: rotate(45deg);
}

.tooltip-top .tooltip-arrow {
  bottom: -4px;
  left: calc(50% - 4px);
  border-radius: 0 0 2px 0;
}

.tooltip-bottom .tooltip-arrow {
  top: -4px;
  left: calc(50% - 4px);
  border-radius: 2px 0 0 0;
}

.tooltip-left .tooltip-arrow {
  right: -4px;
  top: calc(50% - 4px);
  border-radius: 0 2px 0 0;
}

.tooltip-right .tooltip-arrow {
  left: -4px;
  top: calc(50% - 4px);
  border-radius: 0 0 0 2px;
}
</style>
